import { HtmlHTMLAttributes, useCallback, useEffect, useRef, useState } from "react";
import { DataTestId } from "types/enums/testId";
import { ComponentMoleculesNavResource } from "types/graphql/generated";
import { MegaMenuResource } from "../MegaMenuResource";
import { MenuDrawerDesktop } from "../MenuDrawerDesktop";
import { SubMenuGrid } from "../SubMenuGrid";
import { SubMenuPanelGroup } from "../SubMenuPanelGroup";
import { SubMenuTabGroup } from "../SubMenuTabGroup";

interface MegaMenuResourcesProps extends HtmlHTMLAttributes<HTMLElement> {
  navResources: ComponentMoleculesNavResource[];
  subMenuHeightCallback: (index: number) => void;
}

const MegaMenuResources = ({ navResources, subMenuHeightCallback, className }: MegaMenuResourcesProps) => {
  const [hoveringOnIndex, setHoveringOnIndex] = useState<number>(-1);
  const [popoverHeight, setPopoverHeight] = useState(0);
  const refs = useRef<(HTMLElement | null)[]>([]);
  const resizeObserver = useRef<ResizeObserver | null>(null);

  const onMouseEnter = useCallback((index: number) => {
    setHoveringOnIndex(index);
  }, []);

  useEffect(() => {
    subMenuHeightCallback(popoverHeight);
  }, [popoverHeight, subMenuHeightCallback]);

  useEffect(() => {
    resizeObserver.current = new ResizeObserver((entries) => {
      const entry = entries[0];

      setPopoverHeight(entry.target.clientHeight);
    });

    return () => {
      resizeObserver.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    const currentRef = refs.current[hoveringOnIndex];

    if (currentRef) {
      resizeObserver.current?.observe(currentRef);

      const currentMenuItem = document.querySelector(`[data-menu-index="${hoveringOnIndex}"]`);

      if (currentMenuItem && currentMenuItem.nextSibling !== currentRef && currentRef.parentNode) {
        // move submenu in front of current menu link in order to have an accessible tab order
        currentMenuItem?.parentNode?.insertBefore(currentRef.parentNode, currentMenuItem.nextSibling);
      }
    } else {
      setPopoverHeight(0);
    }

    return () => {
      if (currentRef) {
        resizeObserver.current?.unobserve(currentRef);
      }
    };
  }, [hoveringOnIndex]);

  return (
    <nav
      onMouseLeave={() => setHoveringOnIndex(-1)}
      className={className}
      data-testid={DataTestId.DesktopMegaNavResources}
    >
      <div className="flex h-navbar w-full justify-center lg:h-navbar-lg">
        {navResources.map((resource, index) => (
          <MegaMenuResource
            href={resource.url}
            key={resource.id}
            onMouseEnter={() => onMouseEnter(index)}
            onClick={() => onMouseEnter(index)}
            onFocus={() => onMouseEnter(index)}
            isActive={hoveringOnIndex === index}
            data-menu-index={index}
          >
            {resource.label}
          </MegaMenuResource>
        ))}
      </div>

      <MenuDrawerDesktop targetHeight={popoverHeight}>
        {navResources.map(
          (resource, index) =>
            index === hoveringOnIndex &&
            !resource.url && (
              <div
                ref={(element) => (refs.current[index] = element)}
                key={resource.id}
                className="motion-safe:animate-fade"
              >
                <SubMenuGrid layout={resource.subMenuDesktopLayout}>
                  {resource.subMenuTabs.length > 0 ? (
                    <SubMenuTabGroup resource={resource} />
                  ) : (
                    <SubMenuPanelGroup panels={resource.subMenuPanels.slice(0, 3)} />
                  )}
                </SubMenuGrid>
              </div>
            )
        )}
      </MenuDrawerDesktop>
    </nav>
  );
};

export { MegaMenuResources };
